import { useCallback, useState } from 'react';

import { usePermissions } from '@/hooks/usePermissions';
import { Badge } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';

import { useHubspotStatus } from '../hooks/useHubspotStatus';
import { ConnectionControls } from './ConnectionControls';
import { DisconnectConfirmation } from './DisconnectConfirmation';
import { HubspotMappingsManagerView } from './HubspotMappingsManagerView';
import { SyncHealthPanel } from './SyncHealthPanel';

interface ConnectionStatusPanelProps {
  orgId: number | string;
}

export function ConnectionStatusPanel({ orgId }: ConnectionStatusPanelProps) {
  const { data, isLoading } = useHubspotStatus(orgId);
  const { hasPermissionGroup } = usePermissions();
  const [isDisconnectOpen, setIsDisconnectOpen] = useState(false);
  const [managingMappings, setManagingMappings] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const handleDisconnectClick = useCallback(() => {
    setIsDisconnectOpen(true);
  }, []);

  const handleDisconnectClose = useCallback(() => {
    setIsDisconnectOpen(false);
  }, []);

  const handleEditMappings = useCallback(() => {
    setManagingMappings(true);
  }, []);

  const handleBackFromMappings = useCallback(() => {
    setManagingMappings(false);
  }, []);

  const handleToggleSettings = useCallback(() => {
    setShowSettings(prev => !prev);
  }, []);

  if (isLoading) {
    return <LoadingIndicator size='sm' />;
  }

  if (!data?.connected) {
    return null;
  }

  const isAdmin = hasPermissionGroup('ahoy_other_admin');

  if (managingMappings) {
    return (
      <div className='card-refined p-6'>
        <HubspotMappingsManagerView
          orgId={orgId}
          connected
          onBack={handleBackFromMappings}
        />
      </div>
    );
  }

  const isSyncing = data.bulk_completed_at == null;

  return (
    <div className='card-refined p-6'>
      <div className='flex items-start justify-between gap-4'>
        <div className='min-w-0 flex-1'>
          <p className='text-xs font-medium uppercase tracking-wide text-[var(--color-text-tertiary)]'>
            HubSpot portal
          </p>
          <h3 className='truncate text-heading-3 text-[var(--color-text-primary)]'>
            {data.portal_id ?? '—'}
          </h3>
          {typeof data.total_objects_synced === 'number' && (
            <p className='mt-1 text-body-small text-[var(--color-text-secondary)]'>
              {data.total_objects_synced.toLocaleString()} records synced
            </p>
          )}
        </div>
        {isAdmin && (
          <div className='flex shrink-0 items-center gap-2'>
            <Button color='secondary' size='sm' onClick={handleEditMappings}>
              Edit field mappings
            </Button>
            <Button color='secondary' size='sm' onClick={handleToggleSettings}>
              Connection settings
            </Button>
            <Button color='secondary' size='sm' onClick={handleDisconnectClick}>
              Disconnect
            </Button>
          </div>
        )}
      </div>

      <div className='mt-4'>
        {isSyncing ? (
          <div className='flex items-center gap-2'>
            <LoadingIndicator size='xs' inline />
            <span className='text-body-small text-[var(--color-text-secondary)]'>
              Initial import in progress…
            </span>
          </div>
        ) : (
          <Badge type='pill-color' color='success'>
            Initial import complete
          </Badge>
        )}
      </div>

      {showSettings && (
        <div className='mt-4 border-t border-[var(--color-border-secondary)] pt-4'>
          <ConnectionControls orgId={orgId} />
        </div>
      )}

      <SyncHealthPanel orgId={orgId} />

      <DisconnectConfirmation
        orgId={orgId}
        isOpen={isDisconnectOpen}
        onClose={handleDisconnectClose}
      />
    </div>
  );
}
